import { createFileRoute, Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  Alert,
  Badge,
  Button,
  Card,
  FileButton,
  Group,
  Modal,
  MultiSelect,
  Select,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { DateTimePicker } from '@mantine/dates';
import { IconArrowLeft, IconCheck, IconRocket, IconSend, IconUpload } from '@tabler/icons-react';
import { motion } from 'framer-motion';
import { notifications } from '@mantine/notifications';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '../../lib/trpc';
import { extractBodyText, extractHeaderFormat, extractId } from '../../lib/template-introspection';
import {
  TokenTextInput,
  buildContactTokenOptions,
} from '../../components/flow-builder/TokenTextInput';
import type { HeaderInput } from '../../../../api/src/modules/campaigns/schema';

/**
 * Phase A S-2 closure — vendors can finally re-open a saved draft.
 * Calls campaigns.updateDraft to save edits in place; "Schedule now"
 * sets `promoteDraftId` so the same row flips Active → queue rows
 * generated → no Active+Draft duplicate hanging around.
 */
function EditDraft() {
  const { uid } = useParams({ from: '/vendor/campaigns/$uid/edit' });
  const navigate = useNavigate();

  const draft = trpc.campaigns.getDraft.useQuery({ uid });
  const groups = trpc.contacts.groups.list.useQuery();
  const labels = trpc.contacts.labels.list.useQuery();
  const templates = trpc.whatsapp.templates.list.useQuery();
  const customFields = trpc.contacts.customFields.list.useQuery();
  const tokenOptions = useMemo(
    () => buildContactTokenOptions(customFields.data ?? []),
    [customFields.data],
  );

  const [title, setTitle] = useState('');
  const [allContacts, setAllContacts] = useState(false);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [whatsAppTemplateId, setWhatsAppTemplateId] = useState<number | null>(null);
  const [bodyVariables, setBodyVariables] = useState<string[]>([]);
  const [header, setHeader] = useState<HeaderInput | null>(null);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [expiryAt, setExpiryAt] = useState<Date | null>(null);
  const [restrictByLanguage, setRestrictByLanguage] = useState(false);
  const [injectUtm, setInjectUtm] = useState(true);
  const [sendTestOpen, setSendTestOpen] = useState(false);

  // Hydrate form from the draft once it's loaded.
  useEffect(() => {
    if (!draft.data) return;
    setTitle(draft.data.title);
    setWhatsAppTemplateId(draft.data.whatsAppTemplateId);
    setScheduledAt(draft.data.scheduledAt ? new Date(draft.data.scheduledAt) : null);
    setExpiryAt(draft.data.expiryAt ? new Date(draft.data.expiryAt) : null);
    setGroupIds(draft.data.campaignGroups.map((g) => String(g.contactGroupId)));
    setLabelIds(draft.data.campaignLabels.map((l) => String(l.labelId)));
    try {
      const data = draft.data.dataJson ? JSON.parse(draft.data.dataJson) : {};
      setAllContacts(!!data.audience?.allContacts);
      setRestrictByLanguage(!!data.restrictByTemplateLanguage);
      setInjectUtm(data.injectUtm !== false);
      if (Array.isArray(data.bodyVariables)) setBodyVariables(data.bodyVariables);
      if (data.header) setHeader(data.header as HeaderInput);
    } catch {
      /* malformed dataJson — leave defaults */
    }
  }, [draft.data]);

  // Selected template — drives the body-vars editor + header configurator.
  const selectedTemplate = useMemo(() => {
    if (!whatsAppTemplateId) return null;
    return (templates.data ?? []).find((t) => extractId(t) === whatsAppTemplateId) ?? null;
  }, [whatsAppTemplateId, templates.data]);

  const detectedVars = useMemo(() => {
    const body =
      (selectedTemplate as { bodyText?: string } | null)?.bodyText ??
      extractBodyText(selectedTemplate);
    if (!body) return [] as number[];
    const out = new Set<number>();
    for (const m of body.matchAll(/\{\{(\d+)\}\}/g)) out.add(Number(m[1]));
    return [...out].sort((a, b) => a - b);
  }, [selectedTemplate]);

  const headerFormat = useMemo(() => {
    return ((selectedTemplate as { headerFormat?: string } | null)?.headerFormat ??
      extractHeaderFormat(selectedTemplate) ??
      'NONE') as 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
  }, [selectedTemplate]);

  // Resize bodyVariables to detected count when template changes.
  useEffect(() => {
    setBodyVariables((prev) => {
      const next = prev.slice(0, detectedVars.length);
      while (next.length < detectedVars.length) next.push('');
      return next;
    });
  }, [detectedVars.length]);

  const buildInput = () => ({
    title: title.trim() || 'Untitled campaign',
    channel: 'whatsapp' as const,
    whatsAppTemplateId,
    audience: {
      allContacts,
      groupIds: groupIds.map(Number),
      labelIds: labelIds.map(Number),
      contactIds: [] as number[],
    },
    bodyVariables: bodyVariables.length > 0 ? bodyVariables : undefined,
    header: header ?? undefined,
    restrictByTemplateLanguage: restrictByLanguage,
    scheduledAt: scheduledAt ? scheduledAt.toISOString() : null,
    expiryAt: expiryAt ? expiryAt.toISOString() : null,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    recurrenceJson: null,
    injectUtm,
  });

  const update = trpc.campaigns.updateDraft.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Draft saved', color: 'green' });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  const promote = trpc.campaigns.createAndSchedule.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Campaign scheduled — ${r.queued} recipients queued`,
        color: 'green',
      });
      navigate({ to: '/vendor/campaigns/$uid/status', params: { uid: r.uid } });
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  if (draft.isPending) return <Skeleton height={500} />;
  if (draft.isError || !draft.data) {
    return (
      <Stack>
        <Alert color="red">{draft.error?.message ?? 'Draft not found or already promoted.'}</Alert>
        <Button component={Link} to="/vendor/campaigns" leftSection={<IconArrowLeft size={14} />}>
          Back to campaigns
        </Button>
      </Stack>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
      <Stack gap="md">
        <Group>
          <Button
            variant="subtle"
            component={Link}
            to="/vendor/campaigns"
            leftSection={<IconArrowLeft size={14} />}
          >
            Campaigns
          </Button>
        </Group>
        <Group justify="space-between">
          <Title order={2}>Edit draft — {draft.data.title}</Title>
          <Button
            variant="default"
            leftSection={<IconSend size={14} />}
            onClick={() => setSendTestOpen(true)}
            disabled={!whatsAppTemplateId}
          >
            Send test
          </Button>
        </Group>
        <Card>
          <Stack gap="sm">
            <TextInput
              label="Campaign title"
              value={title}
              onChange={(e) => setTitle(e.currentTarget.value)}
            />
            <Switch
              label="All eligible contacts"
              checked={allContacts}
              onChange={(e) => setAllContacts(e.currentTarget.checked)}
            />
            {!allContacts && (
              <Stack gap="xs">
                <MultiSelect
                  label="Contact groups"
                  data={(groups.data ?? []).map((g) => ({ value: String(g.id), label: g.title }))}
                  value={groupIds}
                  onChange={setGroupIds}
                  searchable
                  clearable
                />
                <MultiSelect
                  label="Labels"
                  data={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
                  value={labelIds}
                  onChange={setLabelIds}
                  searchable
                  clearable
                />
              </Stack>
            )}
            <Switch
              label="Restrict to recipients whose language matches the template"
              checked={restrictByLanguage}
              onChange={(e) => setRestrictByLanguage(e.currentTarget.checked)}
            />
            <Select
              label="Approved template"
              data={(templates.data ?? [])
                .filter((t) => t.status === 'APPROVED')
                .map((t) => ({
                  value: String(extractId(t)),
                  label: `${t.name} (${t.language})`,
                }))}
              value={whatsAppTemplateId ? String(whatsAppTemplateId) : null}
              onChange={(v) => setWhatsAppTemplateId(v ? Number(v) : null)}
            />
            {detectedVars.length > 0 && (
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Body variables ({detectedVars.length})
                </Text>
                {detectedVars.map((n, i) => (
                  <TokenTextInput
                    key={n}
                    label={`{{${n}}}`}
                    options={tokenOptions}
                    value={bodyVariables[i] ?? ''}
                    onChange={(value) =>
                      setBodyVariables((prev) => {
                        const next = [...prev];
                        next[i] = value;
                        return next;
                      })
                    }
                  />
                ))}
              </Stack>
            )}
            {headerFormat !== 'NONE' && (
              <CampaignHeaderConfig format={headerFormat} header={header} onChange={setHeader} />
            )}
            <Group grow>
              <DateTimePicker
                label="Send at"
                value={scheduledAt}
                onChange={(v) => setScheduledAt(v ? new Date(v) : null)}
                clearable
              />
              <DateTimePicker
                label="Expire at"
                value={expiryAt}
                onChange={(v) => setExpiryAt(v ? new Date(v) : null)}
                clearable
              />
            </Group>
            <Switch
              label="Auto-inject UTM params on URL-button clicks"
              checked={injectUtm}
              onChange={(e) => setInjectUtm(e.currentTarget.checked)}
            />
            <Group justify="end">
              <Button
                variant="default"
                onClick={() =>
                  update.mutate({
                    campaignId: draft.data!.id,
                    ...buildInput(),
                  })
                }
                loading={update.isPending}
                leftSection={<IconCheck size={14} />}
              >
                Save draft
              </Button>
              <Button
                color="blue"
                onClick={() =>
                  promote.mutate({
                    ...buildInput(),
                    promoteDraftId: draft.data!.id,
                  })
                }
                loading={promote.isPending}
                leftSection={<IconRocket size={14} />}
              >
                Schedule now
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>

      <SendTestDialog
        opened={sendTestOpen}
        onClose={() => setSendTestOpen(false)}
        templateId={whatsAppTemplateId}
        bodyVariables={bodyVariables}
      />
    </motion.div>
  );
}

// ── Helpers ───────────────────────────────

function bufferToBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function CampaignHeaderConfig({
  format,
  header,
  onChange,
}: {
  format: 'NONE' | 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT' | 'LOCATION';
  header: HeaderInput | null;
  onChange: (h: HeaderInput | null) => void;
}) {
  const upload = trpc.whatsapp.templates.uploadSendMedia.useMutation();
  const handleFile = async (file: File | null) => {
    if (!file) return;
    const buf = await file.arrayBuffer();
    const base64 = bufferToBase64(buf);
    upload.mutate(
      { base64, mimeType: file.type, filename: file.name },
      {
        onSuccess: (r) => {
          if (format === 'IMAGE') onChange({ kind: 'image', mediaId: r.mediaId });
          else if (format === 'VIDEO') onChange({ kind: 'video', mediaId: r.mediaId });
          else if (format === 'DOCUMENT')
            onChange({ kind: 'document', mediaId: r.mediaId, filename: file.name });
          notifications.show({ message: 'Media bound to campaign', color: 'green' });
        },
        onError: (err) => notifications.show({ message: err.message, color: 'red' }),
      },
    );
  };
  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        Header — {format}
      </Text>
      {format === 'TEXT' && (
        <TextInput
          label="Header text variable"
          value={header?.kind === 'text' ? (header.variables[0] ?? '') : ''}
          onChange={(e) => onChange({ kind: 'text', variables: [e.currentTarget.value] })}
        />
      )}
      {(format === 'IMAGE' || format === 'VIDEO' || format === 'DOCUMENT') && (
        <Group>
          <FileButton
            onChange={handleFile}
            accept={
              format === 'IMAGE' ? 'image/*' : format === 'VIDEO' ? 'video/*' : 'application/pdf'
            }
          >
            {(props) => (
              <Button
                {...props}
                variant="light"
                leftSection={<IconUpload size={14} />}
                loading={upload.isPending}
              >
                Upload {format.toLowerCase()}
              </Button>
            )}
          </FileButton>
          {header &&
            (header.kind === 'image' || header.kind === 'video' || header.kind === 'document') &&
            header.mediaId && (
              <Badge color="green" variant="light">
                Bound ✓
              </Badge>
            )}
        </Group>
      )}
      {format === 'LOCATION' && (
        <Group grow>
          <TextInput
            label="Latitude"
            value={header?.kind === 'location' ? String(header.latitude) : ''}
            onChange={(e) =>
              onChange({
                kind: 'location',
                latitude: Number(e.currentTarget.value) || 0,
                longitude: header?.kind === 'location' ? header.longitude : 0,
              })
            }
          />
          <TextInput
            label="Longitude"
            value={header?.kind === 'location' ? String(header.longitude) : ''}
            onChange={(e) =>
              onChange({
                kind: 'location',
                latitude: header?.kind === 'location' ? header.latitude : 0,
                longitude: Number(e.currentTarget.value) || 0,
              })
            }
          />
        </Group>
      )}
    </Stack>
  );
}

function SendTestDialog({
  opened,
  onClose,
  templateId,
  bodyVariables,
}: {
  opened: boolean;
  onClose: () => void;
  templateId: number | null;
  bodyVariables: string[];
}) {
  const [phone, setPhone] = useState('');
  const sendTest = trpc.campaigns.sendTest.useMutation({
    onSuccess: () => {
      notifications.show({ message: 'Test sent', color: 'green' });
      onClose();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const placeholderValues: Record<string, string> = {};
  bodyVariables.forEach((v, i) => {
    placeholderValues[String(i + 1)] = v;
  });
  return (
    <Modal opened={opened} onClose={onClose} title="Send test message" centered>
      <Stack>
        <TextInput
          label="Test phone (E.164)"
          placeholder="+919876543210"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
        />
        <Group justify="end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!templateId || !phone}
            loading={sendTest.isPending}
            onClick={() =>
              templateId && sendTest.mutate({ templateId, toPhone: phone, placeholderValues })
            }
          >
            Send
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export const Route = createFileRoute('/vendor/campaigns/$uid/edit')({ component: EditDraft });
